import { useState, useCallback, useRef, useEffect } from 'react';
import type { DatasetRowData, DatasetRowsPage } from '../../../types/dataGrid';
import { getDatasetRows } from '../../../services/dataGridApi';

const PAGE_SIZE = 100;

interface PaginationState {
  pages: Map<number, DatasetRowData[]>; // pageIndex -> rows
  totalRows: number;
  loading: Set<number>; // pageIndices currently being fetched
}

interface PaginationOptions {
  initialRows?: DatasetRowData[];
  initialTotalRows?: number;
}

export function useDataPagination(datasetId: string | null, options?: PaginationOptions) {
  const [state, setState] = useState<PaginationState>(() => {
    // Seed page 0 with initial rows if provided (avoids an API call on mount)
    if (options?.initialRows && options.initialRows.length > 0) {
      const pages = new Map<number, DatasetRowData[]>();
      pages.set(0, options.initialRows);
      return {
        pages,
        totalRows: options.initialTotalRows ?? options.initialRows.length,
        loading: new Set<number>(),
      };
    }
    return { pages: new Map(), totalRows: 0, loading: new Set() };
  });

  // Use refs to avoid stale closures in fetchPage without making it unstable
  const stateRef = useRef(state);
  stateRef.current = state;

  const abortControllers = useRef<Map<number, AbortController>>(new Map());

  const fetchPage = useCallback(
    async (pageIndex: number) => {
      if (!datasetId) return;
      // Use ref to check current state without adding state to deps
      if (stateRef.current.pages.has(pageIndex) || stateRef.current.loading.has(pageIndex)) return;

      setState((prev) => ({
        ...prev,
        loading: new Set([...prev.loading, pageIndex]),
      }));

      try {
        const offset = pageIndex * PAGE_SIZE;
        const result: DatasetRowsPage = await getDatasetRows(datasetId, offset, PAGE_SIZE);

        setState((prev) => {
          const newPages = new Map(prev.pages);
          newPages.set(pageIndex, result.rows);
          const newLoading = new Set(prev.loading);
          newLoading.delete(pageIndex);
          return {
            pages: newPages,
            totalRows: result.total_rows,
            loading: newLoading,
          };
        });
      } catch {
        setState((prev) => {
          const newLoading = new Set(prev.loading);
          newLoading.delete(pageIndex);
          return { ...prev, loading: newLoading };
        });
      }
    },
    [datasetId]
  );

  const ensureRowsLoaded = useCallback(
    (startRow: number, endRow: number) => {
      const startPage = Math.floor(startRow / PAGE_SIZE);
      const endPage = Math.floor(endRow / PAGE_SIZE);

      for (let p = startPage; p <= endPage; p++) {
        fetchPage(p);
      }

      // Pre-fetch adjacent pages
      if (startPage > 0) fetchPage(startPage - 1);
      fetchPage(endPage + 1);
    },
    [fetchPage]
  );

  const getRow = useCallback(
    (rowIndex: number): DatasetRowData | null => {
      const pageIndex = Math.floor(rowIndex / PAGE_SIZE);
      const page = state.pages.get(pageIndex);
      if (!page) return null;
      const offsetInPage = rowIndex - pageIndex * PAGE_SIZE;
      return page[offsetInPage] ?? null;
    },
    [state.pages]
  );

  const isRowLoading = useCallback(
    (rowIndex: number): boolean => {
      const pageIndex = Math.floor(rowIndex / PAGE_SIZE);
      return state.loading.has(pageIndex);
    },
    [state.loading]
  );

  const reset = useCallback(() => {
    // Abort in-flight requests
    abortControllers.current.forEach((controller) => controller.abort());
    abortControllers.current.clear();
    setState({ pages: new Map(), totalRows: 0, loading: new Set() });
  }, []);

  const reload = useCallback(() => {
    reset();
    // Load first page after reset
    if (datasetId) {
      // Use setTimeout to ensure reset state is applied first
      setTimeout(() => fetchPage(0), 0);
    }
  }, [reset, datasetId, fetchPage]);

  // Load first page on mount
  useEffect(() => {
    if (datasetId) {
      fetchPage(0);
    }
    return () => {
      abortControllers.current.forEach((controller) => controller.abort());
    };
    // Only run on datasetId change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasetId]);

  return {
    totalRows: state.totalRows,
    getRow,
    isRowLoading,
    ensureRowsLoaded,
    reset,
    reload,
    pageSize: PAGE_SIZE,
  };
}
